/**
 * Montos de Mercado Público: siempre CLP, UF o UTM.
 * Formato es-CL con separador de miles y conversión con indicadores del día.
 * Mercado Público publica la Unidad de Fomento con el código oficial `CLF`;
 * internamente se normaliza a `UF` para formatear y convertir.
 */

export type MonedaMP = "CLP" | "UF" | "UTM";

export const MONEDAS_MP: { value: MonedaMP; label: string }[] = [
  { value: "CLP", label: "CLP — Peso chileno" },
  { value: "UF", label: "UF — Unidad de Fomento" },
  { value: "UTM", label: "UTM — Unidad Tributaria Mensual" },
];

/** Etiquetas legibles de las únicas unidades válidas en Mercado Público. */
const MONEDA_LABELS: Record<string, string> = {
  CLP: "CLP (Peso chileno)",
  CLF: "UF (Unidad de Fomento)",
  UF: "UF (Unidad de Fomento)",
  UTM: "UTM (Unidad Tributaria Mensual)",
};

/** true si el código es una unidad usada por Mercado Público (CLP/UF/CLF/UTM). */
export function esMonedaMP(codigo: string | null | undefined): boolean {
  const code = (codigo ?? "").toUpperCase();
  return code === "CLP" || code === "UF" || code === "CLF" || code === "UTM";
}

/** `CLF` → `UF`; cualquier código desconocido cae en CLP para no romper cálculos. */
export function normalizeMoneda(codigo: string | null | undefined): MonedaMP {
  const code = (codigo ?? "CLP").toUpperCase();
  if (code === "UF" || code === "CLF") return "UF";
  if (code === "UTM") return "UTM";
  return "CLP";
}

/** "CLF" → "UF (Unidad de Fomento)". Los códigos extranjeros no se muestran como tales. */
export function monedaLabel(codigo: string | null | undefined): string {
  if (!codigo) return "No informado";
  return MONEDA_LABELS[codigo.toUpperCase()] ?? "Monto no expresado en moneda de Mercado Público";
}

/** Formatea un monto con el símbolo o sufijo de su unidad chilena oficial. */
export function formatMontoMoneda(valor: number, codigo: string | null | undefined): string {
  if (!esMonedaMP(codigo)) return "Monto no expresado en moneda de Mercado Público";
  const moneda = normalizeMoneda(codigo);
  if (moneda === "CLP") return `$${formatMonto(valor, "CLP")}`;
  return `${formatMonto(valor, moneda)} ${moneda}`;
}

/**
 * Monto en su moneda de publicación, con el equivalente en pesos al lado.
 *
 * Es la forma en que hay que mostrar un monto en toda la app: el valor se
 * conserva en la unidad en que se publicó —UF, UTM o CLP— y el peso es sólo
 * una referencia calculada con el indicador del día. Poner un signo peso a un
 * número que está en UF lo multiplica por cerca de 39.000 a ojos de quien lee.
 *
 * Los montos publicados en una moneda con la que el sistema no opera se
 * declaran como tales: no se convierten ni se muestran como pesos.
 */
export function montoConEquivalente(
  valor: number | null | undefined,
  codigo: string | null | undefined,
  ind: Indicadores,
): string {
  if (valor == null || !Number.isFinite(valor) || valor <= 0) return "Sin monto informado";
  if (!esMonedaMP(codigo)) {
    return `${formatMonto(valor)} ${(codigo ?? "").toUpperCase()} — moneda no soportada`;
  }
  const moneda = normalizeMoneda(codigo);
  if (moneda === "CLP") return `$${formatMonto(valor, "CLP")}`;
  return `${formatMonto(valor, moneda)} ${moneda} ≈ $${formatMonto(Math.round(toCLP(valor, moneda, ind)), "CLP")}`;
}



/** Origen del dato: bcentral (oficial) > mindicador > persistido > respaldo local. */
export type FuenteIndicador = "bcentral" | "mindicador" | "persistido" | "fallback";

export interface Indicadores {
  uf: number;
  utm: number;
  fecha: string;
  fuente?: FuenteIndicador;
}

export const INDICADORES_FALLBACK: Indicadores = {
  uf: 39000,
  utm: 68000,
  fecha: "",
  fuente: "fallback",
};

/** "20000000" → "20.000.000" (sin decimales para CLP/UTM, hasta 2 para UF). */
export function formatMonto(valor: number, moneda: MonedaMP = "CLP"): string {
  if (!Number.isFinite(valor)) return "";
  return new Intl.NumberFormat("es-CL", {
    maximumFractionDigits: moneda === "UF" ? 2 : 0,
  }).format(valor);
}

/** Quita separadores de miles y deja un número (coma decimal es-CL soportada). */
export function parseMonto(texto: string): number {
  const limpio = texto.replace(/\./g, "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  const n = Number(limpio);
  return Number.isFinite(n) ? n : 0;
}

export function toCLP(valor: number, moneda: MonedaMP, ind: Indicadores): number {
  if (moneda === "UF") return valor * ind.uf;
  if (moneda === "UTM") return valor * ind.utm;
  return valor;
}

export function fromCLP(valorCLP: number, moneda: MonedaMP, ind: Indicadores): number {
  if (moneda === "UF") return ind.uf ? valorCLP / ind.uf : 0;
  if (moneda === "UTM") return ind.utm ? valorCLP / ind.utm : 0;
  return valorCLP;
}

export interface TotalesCLP {
  /** Total consolidado en pesos (UF/UTM convertidas con el indicador del día). */
  totalCLP: number;
  /** Etiqueta principal: "$1.234.567" o "Sin monto informado". */
  label: string;
  /** Desglose por unidad original, para tooltip: ["512,84 UF", "$1.000.000"]. */
  desglose: string[];
  /** Cantidad de montos en códigos no válidos en Mercado Público (ignorados). */
  ignorados: number;
}

/**
 * Consolida montos por moneda en un único total en CLP.
 * Los códigos que no son de Mercado Público (USD/EUR heredados) se ignoran.
 */
export function formatTotalesCLP(
  totales: Record<string, number> | null | undefined,
  ind: Indicadores,
  sinMonto = 0,
): TotalesCLP {
  const entries = Object.entries(totales ?? {}).filter(([, v]) => Number.isFinite(v) && v > 0);
  let totalCLP = 0;
  let ignorados = 0;
  const desglose: string[] = [];
  for (const [codigo, valor] of entries) {
    if (!esMonedaMP(codigo)) {
      ignorados += 1;
      continue;
    }
    const moneda = normalizeMoneda(codigo);
    totalCLP += toCLP(valor, moneda, ind);
    desglose.push(formatMontoMoneda(valor, codigo));
  }
  if (sinMonto > 0) desglose.push(`${sinMonto} sin monto informado`);
  const label = totalCLP > 0 ? `$${formatMonto(Math.round(totalCLP), "CLP")}` : "Sin monto informado";
  return { totalCLP, label, desglose, ignorados };
}


/** "$20.000.000 · 512,8 UF · 294 UTM" para mostrar equivalencias. */
export function equivalencias(valor: number, moneda: MonedaMP, ind: Indicadores): string {
  if (!valor) return "Sin monto";
  const clp = toCLP(valor, moneda, ind);
  const partes: string[] = [];
  if (moneda !== "CLP") partes.push(`$${formatMonto(Math.round(clp), "CLP")} CLP`);
  if (moneda !== "UF") partes.push(`${formatMonto(fromCLP(clp, "UF", ind), "UF")} UF`);
  if (moneda !== "UTM") partes.push(`${formatMonto(fromCLP(clp, "UTM", ind), "UTM")} UTM`);
  return partes.join(" · ");
}
